import React, { useMemo } from 'react';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import useTouchDevice from '@loomstateorg/shared-frontend/hooks/useTouchDevice';
import { desktop, mediumDesktop, tablet } from '@loomstateorg/shared-frontend/utils/screensizes';
import Tile, { Props as TileProps } from './tile';
import Wrapper from 'components/HOC/wrapper';
import { Slider } from '@loomstateorg/shared-frontend';

type WrapperBackground = React.ComponentProps<typeof Wrapper>['background'];

export interface Props {
  tiles: Array<TileProps['tile']>;
  tileLabelClassName?: string;
  /** Background of the brand `Wrapper` (defaults to the brand default). Only `neutral-200` is shared by every brand. */
  background?: 'neutral-200';
  children?: React.ReactNode;
}

const CategorySlider: React.FC<Props> = ({ tiles = [], tileLabelClassName, background }) => {
  const { isTouchDevice } = useTouchDevice();

  const [isDesktopSize] = useMediaQuery(mediumDesktop);

  const showArrows = useMemo(
    () => !isTouchDevice && !((isDesktopSize && tiles.length <= 4) || (!isDesktopSize && tiles.length <= 2)),
    [isDesktopSize, isTouchDevice, tiles.length],
  );

  return (
    <Wrapper
      background={background as WrapperBackground}
      clearDefaultStyles
      className="pl-8 md:pl-12 lg:px-20 xl:px-48"
    >
      <Slider
        containerClassName={showArrows ? 'px-48' : ''}
        slidesPerView={isTouchDevice ? 2.3 : 1.3}
        dots={false}
        spaceBetween={8}
        arrows={showArrows}
        innerArrows
        allowTouchMove={!showArrows}
        slidesOffsetAfter={8}
        breakpoints={{
          [tablet]: {
            spaceBetween: 12,
            slidesPerView: 2.3,
            slidesOffsetAfter: 12,
          },
          [desktop]: {
            slidesOffsetAfter: 0,
          },
          [mediumDesktop]: {
            spaceBetween: 16,
            slidesPerView: 4,
          },
        }}
      >
        {tiles.map((tile, index) => (
          <Tile key={index} tile={tile} labelClassName={tileLabelClassName} index={index} />
        ))}
      </Slider>
    </Wrapper>
  );
};

export default CategorySlider;
